import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, concatMap, filter, from, map, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SignalRService implements OnDestroy {
  private hubConnections = new Map<string, HubConnection>();
  private connectionStates = new Map<string, BehaviorSubject<boolean>>();
  private readonly destroyRef = inject(DestroyRef);

  public ngOnDestroy(): void {
    this.hubConnections.forEach((hubConnection) => hubConnection.stop());
  }

  public startConnection(hubUrl: string): Observable<void> {
    if (this.hubConnections.has(hubUrl)) {
      const existingConnection = this.hubConnections.get(hubUrl);
      if (existingConnection?.state === HubConnectionState.Connected) {
        return of();
      }
    }

    const connectionState$ = new BehaviorSubject<boolean>(false);
    this.connectionStates.set(hubUrl, connectionState$);

    const hubConnection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnections.set(hubUrl, hubConnection);

    hubConnection.onclose(() => {
      connectionState$.next(false);
    });

    return from(hubConnection.start()).pipe(
      tap(() => {
        connectionState$.next(true);
      }),
      catchError((error: Error) => {
        connectionState$.next(false);
        return throwError(() => error);
      }),
    );
  }

  public stopConnection(hubUrl: string): Observable<void> {
    const hubConnection = this.hubConnections.get(hubUrl);
    if (hubConnection) {
      return from(hubConnection.stop()).pipe(
        tap(() => {
          this.hubConnections.delete(hubUrl);
          this.connectionStates.delete(hubUrl);
          console.info(`[SignalR] Connection ${hubUrl} stopped`);
        }),
      );
    }
    return of(void 0);
  }

  public onEvent<T>(hubUrl: string, eventName: string, callback: (data: T) => void): void {
    const hubConnection = this.hubConnections.get(hubUrl);
    if (hubConnection) {
      hubConnection.on(eventName, callback);
    }
  }

  public send(hubUrl: string, methodName: string, ...args: unknown[]): void {
    const hubConnection = this.hubConnections.get(hubUrl);
    if (hubConnection) {
      hubConnection.invoke(methodName, ...args).catch((error) => {
        if (error?.message?.includes('Invocation canceled')) {
          console.warn(`[SignalR] Invocation of '${methodName}' canceled because connection was closed.`);
        } else {
          console.error(error);
        }
      });
    }
  }

  private restartConnectionAndSend(hubUrl: string, methodName: string, entityId: string): Observable<string> {
    return this.stopConnection(hubUrl).pipe(
      switchMap(() => this.startConnection(hubUrl)),
      tap(() => this.send(hubUrl, methodName, entityId)),
      map(() => entityId),
    );
  }

  public setUpSignalrObservableListener<T>(
    inputObservable: Observable<string>,
    hubUrl: string,
    methodName: string,
    eventName: string,
    callBack: (entityId: string, payload: T) => void,
  ): void {
    inputObservable
      .pipe(
        filter((entityId) => entityId !== null),
        concatMap((entityId) => this.restartConnectionAndSend(hubUrl, methodName, entityId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (entityId) => {
          this.onEvent<T>(hubUrl, eventName, (payload: T) => {
            callBack(entityId, payload);
          });
        },
        error: (err) => {
          console.error('Error with SignalR connection:', err);
        },
      });
  }
}
